import { useRef, useState } from 'react';
import { Check } from 'lucide-react';

export interface NoteEntry {
  id: number;
  user_name: string | null;
  content: string;
  created_at: string;
}

function fmtNoteDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' at '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Shared quick-notes thread — auto-growing input + checkmark save, entries listed newest-first
// above it. Built for the equipment/asset detail takeover's NOTES card (Inventory.tsx
// SupplyDetailTakeover), which only ever showed a static "No notes on file." with no way to add
// one. Kept generic (notes/onAdd props, no endpoint baked in) so any other detail page that needs
// the same timestamped-notes-thread pattern can reuse this instead of copy-pasting it — Work Order
// takeover and the Compliance PM task detail page were checked and already have their own
// functioning add-a-note mechanisms (a full multi-note tab backed by work_order_notes, and a
// single blur-to-save field respectively), so neither needed migrating to this component.
export function NotesThread({ notes, onAdd, saving }: {
  notes: NoteEntry[];
  onAdd: (content: string) => Promise<void>;
  saving?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function autoGrow(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  async function handleSave() {
    const content = draft.trim();
    if (!content || saving) return;
    await onAdd(content);
    setDraft('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: notes.length ? 14 : 10 }}>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave(); }}
          placeholder="Add a note…"
          rows={1}
          style={{
            flex: 1, resize: 'none', overflow: 'hidden', minHeight: 36, maxHeight: 200,
            padding: '8px 10px', background: 'var(--bg-base)', border: '1px solid var(--border-default)',
            borderRadius: 8, color: 'var(--text-primary)', fontSize: '0.86rem', lineHeight: 1.4, outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !draft.trim()}
          title="Save note"
          style={{
            flexShrink: 0, width: 36, height: 36, borderRadius: 8,
            border: '1px solid rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.1)',
            color: '#34d399', cursor: draft.trim() && !saving ? 'pointer' : 'default',
            opacity: draft.trim() && !saving ? 1 : 0.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Check size={16} />
        </button>
      </div>
      {notes.length === 0 ? (
        <div style={{ fontSize: '0.86rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No notes on file.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map((n) => (
            <div key={n.id} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.045)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{n.user_name ?? 'Unknown'}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{fmtNoteDate(n.created_at)}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{n.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
